import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

// Move uma casa (cliente) para uma equipe alvo com escopo de tempo.
// scope: "day" | "week" | "month" | "permanent". Temporários voltam sozinhos.
export function MoveClientDialog({
  client,
  targetTeamId,
  targetTeamName,
  onClose,
  onMoved,
}) {
  const [scope, setScope] = useState("permanent");
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (scope === "day" && !day) {
      toast.error("Escolha o dia");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/teams/move-client", {
        client_id: client.id,
        target_team_id: targetTeamId,
        scope,
        day: scope === "day" ? day : undefined,
      });
      if (data.scope === "permanent") {
        toast.success("Casa movida definitivamente para esta equipe");
      } else {
        const rd = data.return_date
          ? new Date(data.return_date + "T00:00:00").toLocaleDateString("pt-BR")
          : "";
        toast.success(
          `Casa movida (${data.moved_jobs} atend.) — volta à equipe original em ${rd}`,
        );
      }
      onMoved && onMoved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao mover");
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid="move-client-dialog"
      >
        <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
              <ArrowRightLeft size={12} /> Mover casa
            </div>
            <div className="text-sm text-slate-800 font-semibold truncate">
              {client?.name} → {targetTeamName}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-2">
          <div className="text-xs font-semibold text-slate-600">
            Por quanto tempo
          </div>
          {[
            ["day", "Somente um dia"],
            ["week", "Uma semana (7 dias)"],
            ["month", "Um mês (30 dias)"],
            ["permanent", "Definitivamente"],
          ].map(([val, lbl]) => (
            <label
              key={val}
              className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer py-0.5"
            >
              <input
                type="radio"
                name="mc-scope"
                checked={scope === val}
                onChange={() => setScope(val)}
                data-testid={`move-client-scope-${val}`}
              />
              {lbl}
            </label>
          ))}
          {scope === "day" && (
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              data-testid="move-client-day"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:border-blue-500 focus:outline-none"
            />
          )}
          <p className="text-xs text-slate-500 pt-1">
            {scope === "permanent"
              ? "A casa sai da equipe atual e passa a pertencer a esta equipe (todos os atendimentos futuros)."
              : "A casa fica nesta equipe durante o período e volta automaticamente para a equipe original depois."}
          </p>
        </div>

        <div className="flex gap-2 px-4 py-3 border-t border-slate-100">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 text-sm font-semibold px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            data-testid="move-client-confirm"
            className="flex-1 text-sm font-semibold px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-1"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Mover
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
